import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { MessageService } from 'primeng/api'
import { BehaviorSubject, Subject, Subscription } from 'rxjs'
import { map } from 'rxjs/operators'
import { MessageToastConfig, TOAST_KEYS, ToastWithTranslateData } from '../models/toast.type'
import { ForceAny } from '@shared/types'

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  $toastChange = new BehaviorSubject<MessageToastConfig | undefined>(undefined)
  $isToastVisible = new Subject<boolean>()
  $subscription = new Subscription()

  constructor(
    public messageService: MessageService,
    public translateService: TranslateService,
  ) {}

  /**
   * ############## @RECOMMENDATION: Better to use showToast() ##############
   * Please Use : @interface MessageToastConfig for the types
   * @function openToastAsText To open toast.
   * @param message The message to show in the toast.
   * @param action  The label for the toast action.
   * @param config  Additional configuration options for the toast.
   */
  openToastAsText(message: ForceAny, action?: string, config?: MessageToastConfig) {
    this.messageService.clear()
    const confirmKeys: TOAST_KEYS[] = [TOAST_KEYS.CONFIRM, TOAST_KEYS.confirm]
    const defaultConfig = {
      ...config,
      detail: message,
      life: 2000,
      closable: !!action?.length,
      severity: config?.severity ?? 'error',
      key: config?.key ?? TOAST_KEYS.default,
      position: config?.position ?? 'top-right',
    }
    this.messageService.add(defaultConfig)
    this.$toastChange.next(defaultConfig)
    this.$isToastVisible.next(false)
    if (config?.key && confirmKeys.includes(config?.key as TOAST_KEYS)) this.$isToastVisible.next(true)
  }

  /**
   * @function openToastWithTranslate To open toast.
   * @param data The message to show in the toast.
   * @param action  The label for the toast action.
   * @param config Additional configuration options for the toast.
   * @returns Observable with translated message.
   */
  openToastWithTranslate(data: ToastWithTranslateData, action?: string, config?: MessageToastConfig) {
    const { translateKey, priorityMessage } = data

    const handleTranslation = (translatedText: string) => {
      const translationToBeShown = priorityMessage || translatedText
      this.openToastAsText(translationToBeShown, action, config)
      return translationToBeShown
    }

    return this.translateService.get(translateKey).pipe(map((msg) => handleTranslation(msg)))
  }

  /**
   *
   * @param config - The configuration for the toast.
   * @description - This method is used to show the toast based on the configuration provided.
   * This is default configured for the confirmation toast - you can pass the configuration as per your requirement.
   */
  showToast(config: Partial<MessageToastConfig>) {
    this.messageService.clear()

    const newConfig: Partial<MessageToastConfig> = {
      ...config,
      life: config?.life ?? 2000,
      closable: config?.closable ?? true,
      severity: config?.severity ?? 'info',
      key: config?.key ?? TOAST_KEYS.CONFIRM,
      position: config?.position ?? 'top-center',
      detail: config?.detail ?? '',
      sticky: config?.sticky ?? true,
      hideCancel: config?.hideCancel ?? false,
    }
    this.messageService.add(newConfig)
    this.$toastChange.next(newConfig)
    this.$isToastVisible.next(true)
  }

  quickToast(msg: string, action: 'success' | 'error' = 'error') {
    this.openToastAsText(msg, '', { severity: action })
  }

  closeToast() {
    this.messageService.clear()
  }
}
